import type { JSX } from 'react'

import { TZDate } from '@date-fns/tz'

import {
  FollowersChartSectionView,
  PostsChartSectionView,
} from '../AllChannelsInsights/sections/ChartSectionViews'
import type {
  FollowersView,
  PostsView,
} from '../AllChannelsInsights/sections/ChartViewToggle'
import { toFollowersSeries } from '../AllChannelsInsights/sections/toFollowersSeries'
import { toPostsBarSeries } from '../AllChannelsInsights/sections/toPostsBarSeries'
import { toPostsStackedBarSeries } from '../AllChannelsInsights/sections/toPostsStackedBarSeries'
import { useMetricChannels } from '../AllChannelsInsights/sections/useMetricChannels'
import { NoDataEmptyState } from '../components/NoDataEmptyState'
import { formatDateRange } from '../utils'
import type { PublicInsightsSample } from './publicInsightsSampleQuery'

export function PublicInsightsCharts({
  sample,
  hasChannelFilter,
  followersView,
  onFollowersViewChange,
  postsView,
  onPostsViewChange,
}: {
  sample: PublicInsightsSample
  hasChannelFilter: boolean
  followersView: FollowersView
  onFollowersViewChange: (value: FollowersView) => void
  postsView: PostsView
  onPostsViewChange: (value: PostsView) => void
}): JSX.Element {
  const channelTimeSeries = sample.allChannelsPostInsights.channelTimeSeries
  const totalChannelCount = channelTimeSeries.length
  const followers = useMetricChannels({
    channelTimeSeries,
    metric: 'followers',
    totalChannelCount,
    hasChannelFilter,
  })
  const posts = useMetricChannels({
    channelTimeSeries,
    metric: 'posts',
    totalChannelCount,
    hasChannelFilter,
  })
  const startDate = new TZDate(sample.currentPeriod.start, 'UTC')
  const endDate = new TZDate(sample.currentPeriod.end, 'UTC')
  const dateLabel = `${formatDateRange(startDate, endDate)} (UTC)`
  const emptyState = (
    <NoDataEmptyState description="No data for this date range." size="small" />
  )

  return (
    <>
      <FollowersChartSectionView
        metricChannels={followers}
        totalChannelCount={totalChannelCount}
        series={toFollowersSeries(
          followers.displayedChannels,
          followers.paletteIndexById,
        )}
        view={followersView}
        onViewChange={onFollowersViewChange}
        startDate={startDate}
        endDate={endDate}
        dateLabel={dateLabel}
        emptyState={emptyState}
        toggleLabel="Followers chart view"
      />
      <PostsChartSectionView
        metricChannels={posts}
        totalChannelCount={totalChannelCount}
        stackedSeries={toPostsStackedBarSeries(
          posts.displayedChannels,
          posts.paletteIndexById,
        )}
        barSeries={toPostsBarSeries(
          posts.displayedChannels,
          posts.paletteIndexById,
        )}
        view={postsView}
        onViewChange={onPostsViewChange}
        startDate={startDate}
        endDate={endDate}
        dateLabel={dateLabel}
        emptyState={emptyState}
        toggleLabel="Posts chart view"
      />
    </>
  )
}
